import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusBadge, ViolationTypeBadge } from './StatusBadge';
import { 
    MapPin, 
    Calendar, 
    Car, 
    Image as ImageIcon, 
    Video, 
    Eye,
    X,
    ChevronLeft,
    ChevronRight,
    ScanLine
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ViolationCard = ({ violation, token, onClick, showUser = false }) => {
    const mediaCount = violation.media_files?.length || 0;
    const imageCount = violation.media_files?.filter(m => m.type === 'image').length || 0;
    const videoCount = violation.media_files?.filter(m => m.type === 'video').length || 0;

    return (
        <Card 
            className="group cursor-pointer transition-colors hover:border-primary/50"
            onClick={onClick}
            data-testid={`violation-card-${violation.id}`}
        >
            <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <Car className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-mono font-bold text-lg tracking-wider">
                                {violation.vehicle_number}
                            </span>
                            {violation.ocr_detected_plate && (
                                <span className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-0.5 rounded">
                                    <ScanLine className="w-3 h-3" />
                                    OCR
                                </span>
                            )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <ViolationTypeBadge type={violation.violation_type} />
                            <StatusBadge status={violation.status} size="sm" />
                        </div>

                        {violation.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                {violation.description}
                            </p>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                            {violation.location && (
                                <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {violation.location}
                                </span>
                            )}
                            <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(violation.created_at), 'MMM d, yyyy • HH:mm')}
                            </span>
                            {mediaCount > 0 && (
                                <span className="flex items-center gap-2">
                                    {imageCount > 0 && (
                                        <span className="flex items-center gap-1">
                                            <ImageIcon className="w-3 h-3" />
                                            {imageCount}
                                        </span>
                                    )}
                                    {videoCount > 0 && (
                                        <span className="flex items-center gap-1">
                                            <Video className="w-3 h-3" />
                                            {videoCount}
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>

                        {showUser && (
                            <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                                Reported by: {violation.user_name} ({violation.user_email})
                            </div>
                        )}
                    </div>

                    <Button 
                        variant="ghost" 
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`view-violation-${violation.id}`}
                    >
                        <Eye className="w-4 h-4" />
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export const MediaViewer = ({ mediaFiles = [], token, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    if (!mediaFiles || mediaFiles.length === 0) {
        return null;
    }

    const currentMedia = mediaFiles[currentIndex];
    const mediaUrl = `${BACKEND_URL}/api/files/${currentMedia.path}?auth=${token}`;

    const goNext = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % mediaFiles.length);
        setLoading(true);
        setError(false);
    };

    const goPrev = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + mediaFiles.length) % mediaFiles.length);
        setLoading(true);
        setError(false);
    };

    return (
        <div 
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={onClose}
        >
            <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 text-white hover:bg-white/20"
                onClick={onClose}
                data-testid="close-media-viewer"
            >
                <X className="w-6 h-6" />
            </Button>

            <div className="relative max-w-4xl max-h-[80vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
                {currentMedia.type === 'video' ? (
                    <video
                        src={mediaUrl}
                        controls
                        autoPlay
                        className="w-full max-h-[80vh] rounded-lg"
                        onLoadedData={() => setLoading(false)}
                        onError={() => { setLoading(false); setError(true); }}
                    />
                ) : (
                    <>
                        {loading && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                            </div>
                        )}
                        {error ? (
                            <div className="flex flex-col items-center justify-center h-64 text-white/60">
                                <ImageIcon className="w-12 h-12 mb-2" />
                                <p>Failed to load image</p>
                            </div>
                        ) : (
                            <img
                                src={mediaUrl}
                                alt={currentMedia.original_name}
                                className={`w-full max-h-[80vh] object-contain rounded-lg transition-opacity ${loading ? 'opacity-0' : 'opacity-100'}`}
                                onLoad={() => setLoading(false)}
                                onError={() => { setLoading(false); setError(true); }}
                            />
                        )}
                    </>
                )}

                {mediaFiles.length > 1 && (
                    <>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                            onClick={goPrev}
                            data-testid="prev-media"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                            onClick={goNext}
                            data-testid="next-media"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </Button>

                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                            {mediaFiles.map((_, idx) => (
                                <button
                                    key={idx}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                        idx === currentIndex ? 'bg-white' : 'bg-white/40'
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentIndex(idx);
                                        setLoading(true);
                                        setError(false);
                                    }}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export const MediaThumbnails = ({ mediaFiles = [], token, onView }) => {
    if (!mediaFiles || mediaFiles.length === 0) {
        return (
            <div className="flex items-center justify-center h-32 bg-muted/30 rounded-lg border border-dashed">
                <div className="text-center text-muted-foreground">
                    <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-50" />
                    <p className="text-xs">No media</p>
                </div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-3 gap-2">
            {mediaFiles.slice(0, 6).map((media, idx) => {
                const mediaUrl = `${BACKEND_URL}/api/files/${media.path}?auth=${token}`;
                
                return (
                    <button
                        key={media.id}
                        className="relative aspect-square rounded-lg overflow-hidden bg-muted group"
                        onClick={() => onView(idx)}
                        data-testid={`media-thumbnail-${idx}`}
                    >
                        {media.type === 'video' ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-muted">
                                <Video className="w-8 h-8 text-muted-foreground" />
                            </div>
                        ) : (
                            <img
                                src={mediaUrl}
                                alt={media.original_name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.querySelector('.fallback')?.classList.remove('hidden');
                                }}
                            />
                        )}
                        <div className="fallback hidden absolute inset-0 flex items-center justify-center bg-muted">
                            <ImageIcon className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                    </button>
                );
            })}
        </div>
    );
};
